import { formatTimeZoneOffset, getTimeZoneName, normalizeIANATimeZone } from '@chronosphereio/core';
import { useMemo } from 'react';
import { useVizFrameworkConfig } from '@/plugin-system/model/config';

export interface UseTimeZoneDetailsProps {
  timeZone?: string;
}

export interface UseTimeZoneDetailsResult {
  /**
   * short variant of the UTC offset. This is used in the time picker input to minimize the amount of
   * space used.
   */
  shortUTCOffset: string;
  /**
   * long variant of the UTC offset. This is used in the tooltip to provide more detail than what
   * is shown in the input.
   */
  longUTCOffset: string;
  /**
   * Time zone in IANA format.
   */
  IANA: string;
  /**
   * Short version of time zone name.
   */
  shortName?: string;
  /**
   * When true, time zone selection is enabled for the user and the time picker can provide
   * context about modifying the time zone.
   */
  timezonePreferencesEnabled?: boolean;
}

/**
 * Returns details about the time zone that will be used to render information in the date time
 * picker.
 */
export const useTimeZoneDetails = ({ timeZone }: UseTimeZoneDetailsProps): UseTimeZoneDetailsResult => {
  const timezonePreferencesEnabled = useVizFrameworkConfig()?.timezonePreferencesEnabled || false;

  return useMemo(() => {
    return {
      shortUTCOffset: formatTimeZoneOffset(timeZone, { variant: 'short' }),
      longUTCOffset: formatTimeZoneOffset(timeZone, { variant: 'long' }),
      IANA: normalizeIANATimeZone(timeZone),
      shortName: getTimeZoneName(timeZone, { variant: 'short' }),
      timezonePreferencesEnabled,
    };
  }, [timezonePreferencesEnabled, timeZone]);
};
